import { AutocompleteWithVirtualList } from '@modules/common/components/AutocompleteWithVirtualList';
import { PrivateSlackChannelConnectionAlert } from '@modules/common/components/PrivateSlackChannelConnectionAlert';
import { SlackChannelVirtualListRow } from '@modules/integrations/components/SlackIntegration/SlackForm/SlackChannelVirtualListRow';
import { AddIntegrationChannelDrawerFooter } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerFooter';
import { AddIntegrationChannelDrawerHeader } from '@modules/integrationsV2/components/AddIntegrationChannelDrawer/AddIntegrationChannelDrawerHeader';
import { IntegrationDescriptionForm } from '@modules/integrationsV2/components/IntegrationDescriptionForm';
import { SelectIntegrationWorkspace } from '@modules/integrationsV2/components/SelectIntegrationWorkspace';
import {
  useFetchSlackChannels,
  useUpdateIntegration,
  useValidateIntegrationForm,
} from '@modules/integrationsV2/hooks';
import { AddIntegrationChannelDrawerContentProps } from '@modules/integrationsV2/types';
import { getDescriptionMapInputForIntegrationChannel } from '@modules/integrationsV2/utils';
import { RefreshRounded } from '@mui/icons-material';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationDescriptionInput,
  SlackChannelInput,
} from 'generated/graphql';
import { useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';

const EMPTY_VALUE: SlackChannelInput = {
  id: '',
  name: '',
};

export const AddSlackIntegrationChannelDrawerContent = ({
  onDrawerClose,
  integrationID,
  integrationType,
  integrationData,
  channelDescriptions,
}: AddIntegrationChannelDrawerContentProps) => {
  const [selectedChannel, setSelectedChannel] =
    useState<SlackChannelInput | null>(null);
  const [selectedWorkspaceID, setSelectedWorkspaceID] = useState(integrationID);
  const [descriptionInput, setDescriptionInput] = useState('');

  const [
    isPollingSlackChannelsInProgress,
    setIsPollingSlackChannelsInProgress,
  ] = useState(false);

  const [updateIntegration, { status: updateIntegrationStatus }] =
    useUpdateIntegration({
      channelType: IntegrationChannelType.Slack,
      integrationType,
    });

  const {
    data: channels = [],
    status: slackChannelsStatus,
    refreshID = undefined,
    pollForSlackChannels,
  } = useFetchSlackChannels(
    selectedWorkspaceID,
    {
      FetchPolicy: 'network-only',
    },
    setIsPollingSlackChannelsInProgress,
  );

  const existingChannels = integrationData.slack?.subscribedChannels ?? [];

  const [
    validateForm,
    {
      isDisabled,
      isDescriptionRequired,
      isValidatingDescription,
      descriptionValidForQuestions,
      validatedDescriptionMessage,
      isPreValidatedDescription,
    },
  ] = useValidateIntegrationForm({
    channelType: IntegrationChannelType.Slack,
    integrationType,
    hasValidFormFields: !!selectedChannel,
    descriptionInput,
    currentDescription: '',
  });

  const integrateSlackChannel = async (
    selectedChannel: SlackChannelInput | null,
    addAnother = false,
  ) => {
    const isValid = await validateForm();

    if (!isValid || !selectedChannel) {
      return;
    }

    let updateDesciptionInput: IntegrationDescriptionInput | undefined;

    if (descriptionInput) {
      updateDesciptionInput = {
        descriptionMap: getDescriptionMapInputForIntegrationChannel(
          channelDescriptions,
          {
            description: descriptionInput.trim(),
            sourceID: selectedChannel.id,
          },
        ),
      };
    }

    // Remove duplicates
    const subscribedChannels = [
      ...new Map(
        [...existingChannels, selectedChannel].map((channel) => [
          channel.id,
          channel,
        ]),
      ).values(),
    ];

    const { errors } = await updateIntegration({
      integrationID: selectedWorkspaceID,
      isActive: true,
      updateData: {
        slack: {
          subscribedChannels,
        },
      },
      updateDescription: updateDesciptionInput,
    });

    if (!!errors?.length) {
      return;
    }

    if (!addAnother) {
      onDrawerClose();
      return;
    }

    setSelectedChannel(null);
    setDescriptionInput('');
  };

  const isLoadingSlackChannels =
    IsLoading(slackChannelsStatus) || isPollingSlackChannelsInProgress;

  const refetchSlackChannels = () => {
    pollForSlackChannels(refreshID);
  };

  const renderInputEndAdornmentAction = () => {
    if (isLoadingSlackChannels) {
      return <CircularProgress size={20} sx={{ color: 'text.secondary' }} />;
    }

    if (refreshID) {
      return (
        <Tooltip title="Refetch all Slack channels" arrow placement="top">
          <IconButton size="small">
            <RefreshRounded onClick={() => refetchSlackChannels()} />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const addedChannelIDsSet = new Set(existingChannels.map((c) => c.id));

  const options = channels.filter(
    (channel) => !addedChannelIDsSet.has(channel.id),
  );

  return (
    <Stack height="100%" justifyContent="space-between">
      <Stack>
        <AddIntegrationChannelDrawerHeader title="Integrate a Slack Channel" />
        <Stack spacing={4} p={3}>
          <SelectIntegrationWorkspace
            selectedWorkspaceID={selectedWorkspaceID}
            onChange={(workspaceID) => setSelectedWorkspaceID(workspaceID)}
            channelType={IntegrationChannelType.Slack}
            integrationType={integrationType}
          />
          <AutocompleteWithVirtualList
            fullWidth
            multiple={false}
            value={selectedChannel ?? EMPTY_VALUE} // Workaround for AutocompleteWithVirtualList not accepting null value, TODO: fix this
            options={options}
            getOptionLabel={(option) => (option.name ? `# ${option.name}` : '')}
            onChange={(_, selected) => setSelectedChannel(selected)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Slack channel"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" alignItems="center">
                        <Box mr={-3}>{renderInputEndAdornmentAction()}</Box>
                        {params.InputProps.endAdornment}
                      </Stack>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option, state) =>
              [props, option, state] as React.ReactNode
            }
            VirtualListRow={(props) => (
              <SlackChannelVirtualListRow
                VirtualListRowProps={props}
                shouldShowCheckbox={false}
              />
            )}
            noOptionsText={
              <>
                <Typography variant="inherit">No matches found</Typography>
                <Typography variant="caption">
                  A new channel may take around 5 minutes to be available.
                </Typography>
              </>
            }
            componentsProps={{ paper: { elevation: 8 } }}
            loading={IsLoading(slackChannelsStatus)}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            clearIcon={null}
          />
          {!!selectedChannel?.isPrivate && (
            <PrivateSlackChannelConnectionAlert channels={[selectedChannel]} />
          )}
          <IntegrationDescriptionForm
            value={descriptionInput}
            onChange={setDescriptionInput}
            isRequired={isDescriptionRequired}
            validatedDescriptionMessage={validatedDescriptionMessage}
            descriptionValidForQuestions={descriptionValidForQuestions}
            isValidationInProgress={isValidatingDescription}
            isPreValidatedDescription={isPreValidatedDescription}
            integrationType={integrationType}
          />
        </Stack>
      </Stack>
      <AddIntegrationChannelDrawerFooter
        onDrawerClose={onDrawerClose}
        onIntegrateAndAddAnother={() =>
          integrateSlackChannel(selectedChannel, true)
        }
        onIntegrate={() => integrateSlackChannel(selectedChannel)}
        isIntegrating={IsLoading(updateIntegrationStatus)}
        isDisabled={isDisabled}
        shouldOpenCancelConfirmationDialog={
          !!selectedChannel || !!descriptionInput
        }
      />
    </Stack>
  );
};
